import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, MapPin, Mail, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  practice_name: "",
  specialty: "",
  providers_needed: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.practice_name.trim()) {
      toast.error("Please fill in your name, email, and practice name.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — our team will reach out within one business day.");
      setForm(INITIAL);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many submissions. Please try again in a few minutes.");
      } else {
        toast.error("Something went wrong sending your enquiry. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid overflow-hidden rounded-[2rem] border border-[#E2E0D5] bg-white lg:grid-cols-5">
          <div className="relative bg-[#0A261E] p-8 sm:p-12 lg:col-span-2">
            <Reveal>
              <Eyebrow dark>Book a Free Consultation</Eyebrow>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold leading-tight tracking-tight text-[#F5F7F6]">
                Tell us about your practice.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-[#A3B8B0]">
                A 20-minute, no-pressure call with a US-based advisor. We map your workflow, show you the
                savings math, and match you with a VMA — usually within 48 hours.
              </p>
              <div className="mt-10 space-y-5">
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-[#EAB308]" />
                  <span className="text-sm text-[#F5F7F6]">Serving clinics nationwide · USA</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-[#EAB308]" />
                  <span className="text-sm text-[#F5F7F6]">vermamedassociates@gmail.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-[#EAB308]" />
                  <span className="text-sm text-[#F5F7F6]">Mon–Fri · 8am–6pm in your time zone</span>
                </div>
              </div>
              <div className="mt-10 overflow-hidden rounded-2xl">
                <img
                  src="https://images.unsplash.com/photo-1758691462743-f9fc9e430d39?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Consultation with a Verma Med advisor"
                  className="h-44 w-full object-cover"
                  loading="lazy"
                  data-testid="contact-image"
                />
              </div>
            </Reveal>
          </div>

          <div className="p-8 sm:p-12 lg:col-span-3">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="enquiry-form">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#0F1E19]" htmlFor="full_name">Full Name *</label>
                  <Input id="full_name" data-testid="enquiry-name-input" placeholder="Dr. Jane Smith" value={form.full_name} onChange={set("full_name")} required />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#0F1E19]" htmlFor="email">Work Email *</label>
                  <Input id="email" type="email" data-testid="enquiry-email-input" placeholder="jane@yourclinic.com" value={form.email} onChange={set("email")} required />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#0F1E19]" htmlFor="phone">Phone Number</label>
                  <Input id="phone" data-testid="enquiry-phone-input" placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#0F1E19]" htmlFor="practice_name">Practice / Clinic Name *</label>
                  <Input id="practice_name" data-testid="enquiry-practice-input" placeholder="Riverside Family Health" value={form.practice_name} onChange={set("practice_name")} required />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#0F1E19]">Specialty</label>
                  <Select value={form.specialty} onValueChange={set("specialty")}>
                    <SelectTrigger data-testid="enquiry-specialty-select">
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {["Family Medicine", "Pediatrics", "Internal Medicine", "Orthopedics", "Cardiology", "Dermatology", "Behavioral Health", "Dental", "Other"].map((s) => (
                        <SelectItem key={s} value={s} data-testid={`specialty-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#0F1E19]">Providers Needing Support</label>
                  <Select value={form.providers_needed} onValueChange={set("providers_needed")}>
                    <SelectTrigger data-testid="enquiry-providers-select">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      {["1 provider", "2–5 providers", "6–10 providers", "10+ providers"].map((s) => (
                        <SelectItem key={s} value={s} data-testid={`providers-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs font-semibold text-[#0F1E19]" htmlFor="message">Message</label>
                  <Textarea id="message" data-testid="enquiry-message-input" rows={4} placeholder="Tell us about your admin workload, EHR, and what you'd love to hand off…" value={form.message} onChange={set("message")} />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="enquiry-submit-btn"
                    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#0A3B2C] px-8 py-3.5 text-sm font-semibold text-[#FAF9F5] transition-all hover:bg-[#072B20] hover:shadow-xl disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Request My Free Consultation
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                  <p className="mt-3 font-mono text-[11px] text-[#4A5D55]">
                    No spam, no obligation. Your details go straight to our US advisory team.
                  </p>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
